/**
 * Planning API client — mirrors iOS AppConfig plan endpoints.
 * POST /plan returns { planId } immediately; poll GET /plan/{planId} for status.
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

const DEV_PLANNING_API = 'https://api.dev.wanderertrip.com';
const PROD_PLANNING_API = 'https://api.wanderertrip.com';

function resolvePlanningApiBase(): string {
  const fromEnv = import.meta.env.VITE_PLANNING_API_BASE_URL?.replace(/\/$/, '');
  if (fromEnv) return fromEnv;

  const base = API_BASE_URL.toLowerCase();
  if (base.includes('api.wanderertrip.com') && !base.includes('dev')) {
    return PROD_PLANNING_API;
  }
  // Local/dev execute-api and api.dev.* both target the planning API host iOS uses in development.
  if (base.includes('dev') || base.includes('execute-api') || base.includes('localhost')) {
    return DEV_PLANNING_API;
  }
  return PROD_PLANNING_API;
}

export const PLANNING_API_BASE_URL = resolvePlanningApiBase();

export function planCreateURL(): string {
  return `${PLANNING_API_BASE_URL}/plan`;
}

export function planStatusURL(planId: string): string {
  return `${PLANNING_API_BASE_URL}/plan/${encodeURIComponent(planId)}`;
}

/** `GET /viator/products?cityVisitId=&limit=&cursor=` — mirrors iOS AppConfig.viatorProductsURL */
export function viatorProductsURL(
  cityVisitId: string,
  options?: { limit?: number; cursor?: string | null }
): string {
  const params = new URLSearchParams();
  params.set('cityVisitId', cityVisitId);
  if (options?.limit != null) {
    params.set('limit', String(options.limit));
  }
  if (options?.cursor) {
    params.set('cursor', options.cursor);
  }
  return `${PLANNING_API_BASE_URL}/viator/products?${params.toString()}`;
}

export interface PlanCreateCityVisit {
  id: string;
  cityName: string;
  latitude: number;
  longitude: number;
  startDate: string;
  endDate: string;
}

export interface PlanCreateRequest {
  cityVisit: PlanCreateCityVisit;
  travelerPreferences: string;
  preferredActivities?: string[];
  sightseeingStyle?: string;
}

/** Parses planId from POST /plan JSON (`{ planId }` or any single string value). */
export function parsePlanId(body: unknown): string | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const record = body as Record<string, unknown>;
  if (typeof record.planId === 'string' && record.planId.trim()) {
    return record.planId.trim();
  }
  const values = Object.values(record);
  if (values.length === 1 && typeof values[0] === 'string' && values[0].trim()) {
    return values[0].trim();
  }
  return null;
}

export function hasValidPlanIdResponse(httpStatus: number, planId: string | null): boolean {
  return httpStatus === 200 && Boolean(planId?.trim());
}

/**
 * POST /plan — returns planId on success.
 */
export async function createPlanJob(request: PlanCreateRequest): Promise<{
  ok: boolean;
  status: number;
  planId: string | null;
  errorMessage?: string;
}> {
  const headers = await buildAuthHeaders(true);
  const response = await fetch(planCreateURL(), {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  const planId = parsePlanId(parsed);
  if (hasValidPlanIdResponse(response.status, planId)) {
    return { ok: true, status: response.status, planId };
  }

  const errorMessage =
    parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? String(
          (parsed as Record<string, unknown>).detail ??
            (parsed as Record<string, unknown>).title ??
            (parsed as Record<string, unknown>).message ??
            JSON.stringify(parsed).slice(0, 200)
        )
      : `HTTP ${response.status}`;

  return {
    ok: false,
    status: response.status,
    planId: null,
    errorMessage,
  };
}
